import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Step 1: Flight Details
export interface FlightDetailsData {
  confirmation_number?: string;
  flight_number?: string;
  departure_airport_id?: number;
  departureAirportName?: string; // For display purposes
  departureCountryName?: string; // Country name for category fetch
  departureCountryCode?: string;
  destination_airport_id?: number;
  destinationAirportName?: string; // For display purposes
  destinationCountryName?: string;
  destinationCountryCode?: string;
  departure_date?: string;
  departure_time?: string;
  arrival_date?: string;
  arrival_time?: string;
}

// Category selection for itinerary
export interface SelectedCategory {
  id: string;
  name: string;
}

// Step 2: Luggage Details
export interface LuggageDetailsData {
  weight?: number;
  weightUnit?: 'kg' | 'lbs';
  weightDisplayUnit?: 'kg' | 'lbs'; // Display unit last selected for the weight field
  price?: number;
  corridor_id?: string;
  selectedCategories?: SelectedCategory[];
  sender_drop_off_address?: string;
  receiver_drop_off_address?: string;
  drop_of_date?: string;
  drop_of_time?: string;
  pick_up_date?: string;
  pick_up_time?: string;
}


export interface UploadTicketData {
  // Store file metadata for restoration
  ticket_file_metadata?: Array<{
    name: string;
    size: number;
    type: string;
  }>;
  passport_file_metadata?: Array<{
    name: string;
    size: number;
    type: string;
  }>;
  // Store actual File objects temporarily (not serializable to localStorage)
  ticket_files?: File[];
  passport_files?: File[];
  // Store uploaded image IDs from server
  ticket_ids?: number[];
  passport_ids?: number[];
}

// Complete form data interface
export interface ItineraryFormData {
  itineraryName?: string; // NEW: Add itinerary name field
  flightDetails: FlightDetailsData;
  luggageDetails: LuggageDetailsData;
  uploadTicket: UploadTicketData;
}

interface ItineraryStore {
  currentStep: number;
  formData: ItineraryFormData;
  createdItineraryId: string | null;

  // Navigation actions
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;

  // Data update actions
  updateFlightDetails: (data: Partial<FlightDetailsData>) => void;
  updateLuggageDetails: (data: Partial<LuggageDetailsData>) => void;
  updateUploadTicket: (data: Partial<UploadTicketData>) => void;
  updateItineraryName: (name: string) => void;
  setCreatedItineraryId: (id: string | null) => void;

  // Reset actions
  resetForm: () => void;
  resetStep: (step: 1 | 2 | 3) => void;
}

const initialFormData: ItineraryFormData = {
  itineraryName: undefined,
  flightDetails: {},
  luggageDetails: {},
  uploadTicket: {},
};

export const useItineraryStore = create<ItineraryStore>()(
  devtools(
    (set) => ({
      currentStep: 1,
      formData: initialFormData,
      createdItineraryId: null,

      setCurrentStep: (step) => set({ currentStep: step }),

      goToNextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, 4),
        })),

      goToPreviousStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 1),
        })),

      updateFlightDetails: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            flightDetails: { ...state.formData.flightDetails, ...data },
          },
        })),

      updateLuggageDetails: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            luggageDetails: { ...state.formData.luggageDetails, ...data },
          },
        })),

      updateUploadTicket: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            uploadTicket: { ...state.formData.uploadTicket, ...data },
          },
        })),

      updateItineraryName: (name) =>
        set((state) => ({
          formData: {
            ...state.formData,
            itineraryName: name,
          },
        })),

      setCreatedItineraryId: (id) => set({ createdItineraryId: id }),

      resetForm: () => set({ currentStep: 1, formData: initialFormData, createdItineraryId: null }),

      resetStep: (step) =>
        set((state) => {
          const newFormData = { ...state.formData };
          if (step === 1) newFormData.flightDetails = {};
          if (step === 2) newFormData.luggageDetails = {};
          if (step === 3) newFormData.uploadTicket = {};
          return { formData: newFormData };
        }),
    }),
    {
      name: 'ItineraryStore',
      serialize: { options: true } // Prevent serialization of File objects
    }
  )
);